<template>
  <modal-template ref="modal" :title="title" type-modal="export_file_modal">
    <div class="modal-content__main">
      <d-form>
        <d-form-row>
          <d-col cols="6" class="mb-2">
            <d-checkbox
              class="custom-toggle-sm"
              v-for="(enabled, type) in model.selectedTypes"
              :key="type"
              v-model="model.selectedTypes[type]"
            >
              {{ model.typeName[type] }}
            </d-checkbox>
          </d-col>
          <d-col cols="6" class="mb-2">
            <label> Formato do arquivo:</label>
            <div class="ml-2">
              <d-radio
                v-model="formatMode"
                name="format-mode"
                value="svg"
                class="fs-6 text-muted"
              >
                SVG
              </d-radio>

              <d-radio
                v-model="formatMode"
                name="format-mode"
                value="eps"
                class="fs-6 text-muted"
              >
                EPS
              </d-radio>
            </div>
          </d-col>
        </d-form-row>
      </d-form>
    </div>
    <div slot="actions" class="modal-content__control">
      <d-button
        type="button"
        @click="onCancel()"
        class="btn btn-sm btn-secondary mr-2"
      >
        Cancelar
      </d-button>
      <d-button
        type="button"
        @click="onConfirm()"
        class="btn btn-sm btn-primary"
      >
        Confirmar
      </d-button>
    </div>
  </modal-template>
</template>

<script>
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { mapActions } from 'vuex';

import generateHuntingWordsSVG from '_utils/svgGenerator';
import { b64toBlob } from '_utils';
import ModalTemplate from '_shared/Modal.vue';
import ImageService from '_services/rest/modules/image';
import config from '_config';
import formatErrors from '_common/mixins/FormatErrors';

export default {
  name: 'export-file-modal',
  mixins: [formatErrors],
  components: {
    ModalTemplate,
  },
  props: {
    rows: {
      type: Number,
      required: true,
    },
    columns: {
      type: Number,
      required: true,
    },
    table: {
      type: Array,
      required: false,
      default: () => [],
    },
    highlightedWords: {
      type: Array,
      required: false,
      default: () => [],
    },
    fileName: {
      type: String,
      required: true,
    },
    children: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      model: {
        selectedTypes: {
          listWord: true,
          enigma: true,
          solution: true,
        },
        typeName: {
          enigma: 'Enigma',
          listWord: 'Lista de palavras',
          solution: 'Solução',
        },
      },
      formatMode: 'eps',
      title: 'Exportar arquivos',
    };
  },
  methods: {
    ...mapActions(['setToggle', 'activeAlert', 'showLoader', 'hideLoader']),
    onCancel() {
      this.setToggle({ type: 'export_file_modal', bool: false });
    },
    generateNumericId() {
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 100000);
      return `${timestamp}${random}`;
    },
    async downloadExportedFiles(zip, filenamePrefix) {
      const files = Object.entries(zip.files);

      if (files.length === 1) {
        const [fileName, zipEntry] = files[0];
        const blob = await zipEntry.async('blob');
        saveAs(blob, fileName);
      } else {
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, `${filenamePrefix}.zip`);
      }
    },
    processGameExport({
      table,
      rows,
      columns,
      highlightedWords,
      baseName,
      svgList,
      zip,
      selectedTypes,
    }) {
      if (selectedTypes.enigma) {
        const svg = generateHuntingWordsSVG(table, rows, columns);
        svgList.push({ id: `${baseName}_Enigma`, svg });
      }

      if (selectedTypes.solution) {
        const svg = generateHuntingWordsSVG(table, rows, columns, {
          highlightedWords,
        });
        svgList.push({ id: `${baseName}_Solucao`, svg });
      }

      if (selectedTypes.listWord && highlightedWords?.length) {
        const wordList = highlightedWords.map(word => word.word).sort((a, b) => a.localeCompare(b));
        const content = wordList.join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        zip.file(`${baseName}_Palavras.txt`, blob);
      }
    },
    splitIntoBatches(items, batchSize) {
      const batches = [];
      for (let i = 0; i < items.length; i += batchSize) {
        batches.push(items.slice(i, i + batchSize));
      }
      return batches;
    },
    prepareSvgFiles() {
      const { selectedTypes } = this.model;
      const svgFiles = [];
      const zipArchive = new JSZip();
      const exportId = this.generateNumericId();

      if (this.children?.length > 0) {
        this.children.forEach((child, index) => {
          const baseFileName = `${index + 1}`;
          this.processGameExport({
            table: child.table,
            rows: this.rows,
            columns: this.columns,
            highlightedWords: child.settings.placedWords,
            baseName: baseFileName,
            svgList: svgFiles,
            zip: zipArchive,
            selectedTypes,
          });
        });
      } else {
        const baseFileName = this.fileName;
        this.processGameExport({
          table: this.table,
          rows: this.rows,
          columns: this.columns,
          highlightedWords: this.highlightedWords,
          baseName: baseFileName,
          svgList: svgFiles,
          zip: zipArchive,
          selectedTypes,
        });
      }

      return { exportId, svgFiles, zipArchive };
    },
    async convertSvgBatchToEps(svgFiles, zipArchive) {
      const batchLimit = config.SVG_UPLOAD_LIMIT;
      const svgBatches = this.splitIntoBatches(svgFiles, batchLimit);

      let nextProgress = 0;
      const total = svgFiles.length;

      for (const batch of svgBatches) {
        try {
          nextProgress += batch.length;
          this.showLoader(`Convertendo ${nextProgress > total ? total : nextProgress} de ${total} arquivos`);

          const convertedFiles = await ImageService.convertMultipleSVGsToEPS(batch);

          for (const file of convertedFiles) {
            if (file.epsBase64) {
              const blob = b64toBlob(file.epsBase64, 'application/postscript');
              zipArchive.file(`${file.id}.eps`, blob);
            } else {
              console.warn(`Erro ao converter ${file.id}: ${file.error}`);
              this.activeAlert({
                message: `Erro ao converter ${file.id}: ${file.error}`,
                theme: 'danger',
              });
              this.onCancel();
              return false;
            }
          }
        } catch (error) {
          const formattedErrors = this.formatErrors(error);
          this.activeAlert({ message: formattedErrors, theme: 'danger' });
          this.onCancel();
          return false;
        }
      }

      this.showLoader('Finalizando exportação');
      return true;
    },
    addSvgFilesToZip(svgFiles, zipArchive) {
      svgFiles.forEach(file => {
        zipArchive.file(`${file.id}.svg`, file.svg);
      });
    },
    async finalizeExport(zipArchive, exportId) {
      await this.downloadExportedFiles(zipArchive, `${this.fileName}-${exportId}`);
      this.onCancel();
    },
    async exportSvgFiles() {
      const { exportId, svgFiles, zipArchive } = this.prepareSvgFiles();

      if (svgFiles.length > 0) {
        if (this.formatMode === 'eps') {
          const success = await this.convertSvgBatchToEps(svgFiles, zipArchive);
          if (!success) return;
        } else {
          this.addSvgFilesToZip(svgFiles, zipArchive);
        }
      }

      await this.finalizeExport(zipArchive, exportId);
      this.hideLoader();
    },
    onConfirm() {
      this.exportSvgFiles();
    },
  },
};
</script>
